import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Search, MessageSquare, Phone, Check, CheckCheck, 
  Clock, AlertCircle, Building2, User, ChevronLeft
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MessagesPage = () => {
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [clients, setClients] = useState({});
  const [mobileView, setMobileView] = useState('list'); // 'list' or 'chat'
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConversations();
    fetchClients();
    const interval = setInterval(fetchConversations, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.phone_number, selectedConversation.client_id);
    }
  }, [selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      const clientMap = {};
      response.data.forEach(c => { clientMap[c.id] = c; });
      setClients(clientMap);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/conversations`);
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (phoneNumber, clientId) => {
    try {
      const response = await axios.get(`${API}/conversations/${encodeURIComponent(phoneNumber)}/messages`, {
        params: { client_id: clientId }
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const selectConversation = (conv) => {
    setSelectedConversation(conv);
    setMobileView('chat');
  };

  const goBackToList = () => {
    setMobileView('list');
    setSelectedConversation(null);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'queued':
        return <Clock className="h-3 w-3 text-[#667781]" />;
      case 'sent':
        return <Check className="h-3 w-3 text-[#667781]" />;
      case 'delivered':
        return <CheckCheck className="h-3 w-3 text-[#667781]" />;
      case 'read':
        return <CheckCheck className="h-3 w-3 text-[#0066CC]" />;
      case 'failed':
        return <AlertCircle className="h-3 w-3 text-[#EF4444]" />;
      default:
        return <Clock className="h-3 w-3 text-[#667781]" />;
    }
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length > 10) {
      return `+${cleaned.slice(0, 2)} ${cleaned.slice(2, 5)} ${cleaned.slice(5, 8)}-${cleaned.slice(8)}`;
    }
    return phone;
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Ayer';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('es-AR', { weekday: 'short' });
    }
    return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });
  };

  const filteredConversations = conversations.filter(conv =>
    conv.phone_number.includes(searchTerm) ||
    (conv.client_name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0066CC]"></div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-4rem)] flex bg-white rounded-lg shadow-sm overflow-hidden" data-testid="messages-page">
      {/* Conversations List */}
      <div className={`w-full md:w-[350px] border-r border-[#E9EDEF] flex flex-col ${
        mobileView === 'chat' ? 'hidden md:flex' : 'flex'
      }`}>
        {/* List Header */}
        <div className="p-4 border-b border-[#E9EDEF] bg-[#F0F2F5]">
          <h2 className="text-xl font-semibold text-[#2C3E50] mb-3">Mensajes</h2>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#6B7B8C]" />
            <Input
              placeholder="Buscar conversación..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white border-0"
              data-testid="messages-search-input"
            />
          </div>
        </div>

        {/* Conversations */}
        <ScrollArea className="flex-1">
          {filteredConversations.length === 0 ? (
            <div className="text-center py-12 px-4">
              <MessageSquare className="h-12 w-12 mx-auto text-[#E9EDEF] mb-3" />
              <p className="text-[#6B7B8C]">No hay conversaciones</p>
            </div>
          ) : (
            <div>
              {filteredConversations.map((conv) => (
                <div
                  key={`${conv.client_id}-${conv.phone_number}`}
                  onClick={() => selectConversation(conv)}
                  className={`flex items-center px-4 py-3 cursor-pointer hover:bg-[#F0F2F5] transition-colors border-b border-[#E9EDEF] ${
                    selectedConversation?.phone_number === conv.phone_number && 
                    selectedConversation?.client_id === conv.client_id
                      ? 'bg-[#F0F2F5] border-l-4 border-l-[#0066CC]' 
                      : ''
                  }`}
                  data-testid={`conversation-item-${conv.phone_number}`}
                >
                  {/* Avatar */}
                  <div className="h-12 w-12 rounded-full bg-[#00A5E0] flex items-center justify-center flex-shrink-0">
                    <User className="h-6 w-6 text-white" />
                  </div>
                  
                  {/* Info */}
                  <div className="ml-3 flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-[#2C3E50] truncate">
                        {formatPhoneNumber(conv.phone_number)}
                      </span>
                      <span className="text-xs text-[#6B7B8C] ml-2 flex-shrink-0">
                        {formatTime(conv.last_message_time)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-sm text-[#6B7B8C] truncate">{conv.last_message}</p>
                      {conv.message_count > 0 && (
                        <span className="ml-2 bg-[#25D366] text-white text-xs rounded-full px-2 py-0.5 flex-shrink-0">
                          {conv.message_count}
                        </span>
                      )}
                    </div>
                    {conv.client_name && (
                      <div className="flex items-center mt-1">
                        <Building2 className="h-3 w-3 text-[#6B7B8C] mr-1" />
                        <span className="text-xs text-[#6B7B8C]">{conv.client_name}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </div>

      {/* Chat Area */}
      <div className={`flex-1 flex flex-col ${
        mobileView === 'list' ? 'hidden md:flex' : 'flex'
      }`}>
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="h-16 px-4 flex items-center border-b border-[#E9EDEF] bg-[#F0F2F5]">
              <Button 
                variant="ghost" 
                size="icon" 
                className="md:hidden mr-2"
                onClick={goBackToList}
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <div className="h-10 w-10 rounded-full bg-[#00A5E0] flex items-center justify-center">
                <User className="h-5 w-5 text-white" />
              </div>
              <div className="ml-3">
                <p className="font-medium text-[#2C3E50]">
                  {formatPhoneNumber(selectedConversation.phone_number)}
                </p>
                <div className="flex items-center text-xs text-[#6B7B8C]">
                  <Building2 className="h-3 w-3 mr-1" />
                  {selectedConversation.client_name || clients[selectedConversation.client_id]?.name || 'Cliente'}
                </div>
              </div>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 chat-bg p-4">
              <div className="space-y-2 max-w-3xl mx-auto">
                {messages.map((msg, index) => (
                  <div
                    key={msg.id}
                    className="flex justify-end message-enter"
                    style={{ animationDelay: `${index * 0.05}s` }}
                    data-testid={`message-bubble-${msg.id}`}
                  >
                    <div className="bubble-outgoing max-w-[75%] px-3 py-2 shadow-sm">
                      <p className="text-[#111B21] text-sm whitespace-pre-wrap break-words">
                        {msg.message}
                      </p>
                      <div className="flex items-center justify-end mt-1 space-x-1">
                        <span className="text-[11px] text-[#667781]">
                          {new Date(msg.created_at).toLocaleTimeString('es-AR', { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </span>
                        {getStatusIcon(msg.status)}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Footer - Info only (no send) */}
            <div className="h-14 px-4 flex items-center justify-center border-t border-[#E9EDEF] bg-[#F0F2F5]">
              <p className="text-sm text-[#6B7B8C]">
                Vista de solo lectura - Los mensajes se envían vía API
              </p>
            </div>
          </>
        ) : (
          /* Empty State */
          <div className="flex-1 flex flex-col items-center justify-center bg-[#F0F2F5]">
            <div className="text-center">
              <div className="h-20 w-20 rounded-full bg-[#E9EDEF] flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="h-10 w-10 text-[#6B7B8C]" />
              </div>
              <h3 className="text-xl font-medium text-[#2C3E50] mb-2">SmartBoat MessageHub</h3>
              <p className="text-[#6B7B8C] max-w-sm">
                Selecciona una conversación para ver los mensajes enviados a través de la API
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessagesPage;
